/**
 * Toast 通知组件 - 用于显示成功、错误、警告、信息消息
 */

import { createContext, useContext, useState, useCallback, ReactNode } from 'react';
import { errorHandler, formatErrorForDisplay } from '@foodbook/core-logic';

type ToastType = 'success' | 'error' | 'warning' | 'info';

export interface Toast {
  id: string;
  type: ToastType;
  title: string;
  message?: string;
  duration?: number;
  onClose?: () => void;
}

interface ToastContextValue {
  toasts: Toast[];
  showToast: (toast: Omit<Toast, 'id'>) => void;
  showSuccess: (title: string, message?: string) => void;
  showError: (error: Error | unknown, context?: string) => void;
  showWarning: (title: string, message?: string) => void;
  showInfo: (title: string, message?: string) => void;
  removeToast: (id: string) => void;
  clearToasts: () => void;
}

const ToastContext = createContext<ToastContextValue | undefined>(undefined);

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);

  const showToast = useCallback((toast: Omit<Toast, 'id'>) => {
    const id = `toast-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
    const newToast: Toast = {
      id,
      duration: 5000,
      ...toast,
    };

    setToasts((prev) => [...prev, newToast]);

    // 自动移除
    if (newToast.duration && newToast.duration > 0) {
      setTimeout(() => {
        removeToast(id);
      }, newToast.duration);
    }
  }, []);

  const showSuccess = useCallback((title: string, message?: string) => {
    showToast({ type: 'success', title, message });
  }, [showToast]);

  const showError = useCallback((error: Error | unknown, context?: string) => {
    const appError = errorHandler.handleError(error, { context });
    const errorDisplay = formatErrorForDisplay(appError);

    showToast({
      type: 'error',
      title: errorDisplay.message,
      message: errorDisplay.isDev ? `错误代码: ${errorDisplay.code}` : undefined,
      duration: 8000, // 错误消息显示更长时间
    });
  }, [showToast]);

  const showWarning = useCallback((title: string, message?: string) => {
    showToast({ type: 'warning', title, message });
  }, [showToast]);

  const showInfo = useCallback((title: string, message?: string) => {
    showToast({ type: 'info', title, message });
  }, [showToast]);

  const removeToast = useCallback((id: string) => {
    setToasts((prev) => prev.filter((toast) => toast.id !== id));
  }, []);

  const clearToasts = useCallback(() => {
    setToasts([]);
  }, []);

  return (
    <ToastContext.Provider
      value={{
        toasts,
        showToast,
        showSuccess,
        showError,
        showWarning,
        showInfo,
        removeToast,
        clearToasts,
      }}
    >
      {children}
      <ToastContainer toasts={toasts} onRemove={removeToast} />
    </ToastContext.Provider>
  );
}

function useToastContext(): ToastContextValue {
  const context = useContext(ToastContext);
  if (!context) {
    throw new Error('useToast must be used within a ToastProvider');
  }
  return context;
}

/**
 * Toast Hook
 */
export function useToast() {
  return useToastContext();
}

/**
 * Toast 容器组件
 */
function ToastContainer({ toasts, onRemove }: { toasts: Toast[]; onRemove: (id: string) => void }) {
  if (toasts.length === 0) return null;

  return (
    <div className="fixed top-4 right-4 z-50 flex flex-col gap-2 max-w-md w-full pointer-events-none">
      {toasts.map((toast) => (
        <ToastItem key={toast.id} toast={toast} onRemove={onRemove} />
      ))}
    </div>
  );
}

/**
 * 单个 Toast 组件
 */
function ToastItem({ toast, onRemove }: { toast: Toast; onRemove: (id: string) => void }) {
  const getToastStyles = () => {
    switch (toast.type) {
      case 'success':
        return 'bg-green-100 dark:bg-green-900 border-green-500 dark:border-green-600';
      case 'error':
        return 'bg-red-100 dark:bg-red-900 border-red-500 dark:border-red-600';
      case 'warning':
        return 'bg-yellow-100 dark:bg-yellow-900 border-yellow-500 dark:border-yellow-600';
      case 'info':
        return 'bg-blue-100 dark:bg-blue-900 border-blue-500 dark:border-blue-600';
      default:
        return 'bg-gray-100 dark:bg-gray-800 border-gray-500 dark:border-gray-600';
    }
  };

  const getToastIcon = () => {
    switch (toast.type) {
      case 'success':
        return '✅';
      case 'error':
        return '❌';
      case 'warning':
        return '⚠️';
      case 'info':
        return 'ℹ️';
      default:
        return '📋';
    }
  };

  return (
    <div
      className={`
        ${getToastStyles()}
        pointer-events-auto
        bg-opacity-95 dark:bg-opacity-95
        border-2
        border-l-4
        rounded-lg
        shadow-lg
        p-4
        animate-slide-in-right
        transition-all
        hover:shadow-xl
        max-w-md
      `}
    >
      <div className="flex items-start gap-3">
        <span className="text-2xl flex-shrink-0">{getToastIcon()}</span>
        <div className="flex-1 min-w-0">
          <h4 className="font-bold text-gray-900 dark:text-gray-100 mb-1 font-mono text-sm">
            {toast.title}
          </h4>
          {toast.message && (
            <p className="text-sm text-gray-700 dark:text-gray-300 font-mono">
              {toast.message}
            </p>
          )}
        </div>
        <button
          onClick={() => onRemove(toast.id)}
          className="text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors flex-shrink-0"
          aria-label="关闭"
        >
          ✕
        </button>
      </div>
    </div>
  );
}

// 在 index.css 中添加动画
// @keyframes slide-in-right {
//   from {
//     transform: translateX(100%);
//     opacity: 0;
//   }
//   to {
//     transform: translateX(0);
//     opacity: 1;
//   }
// }

/**
 * 旧版 Toast 组件（兼容旧接口）
 * @deprecated 使用 ToastProvider 和 useToast Hook 替代
 */
export interface ToastProps {
  show: boolean;
  message: string;
}

export function Toast({ show, message }: ToastProps) {
  if (!show) return null;

  return (
    <div className="fixed bottom-4 left-1/2 transform -translate-x-1/2 z-50">
      <div className="bg-wood-light dark:bg-wood-dark border-2 border-wood-dark dark:border-wood-light rounded-lg shadow-lg px-6 py-3">
        <p className="text-wood-dark dark:text-wood-light font-mono text-sm">{message}</p>
      </div>
    </div>
  );
}